/**
 * Page-level JSON-LD for the per-endpoint pages (/api/:apiId/:endpointId).
 *
 * These are the deepest and most numerous pages on the site - 313 of the 462
 * prerendered pages - and until now they carried no page-level node at all,
 * only the sitewide Organization + WebSite graph every page gets. A crawler
 * could see the URL but nothing about what the page offers.
 *
 * Emits Service + Offer + BreadcrumbList, mirroring apiSchema.ts one level
 * down. The price is the customer-facing per-request USD figure and comes from
 * `cheapest()`, the same owner the visible price hero renders, so schema and
 * page can never disagree. AnyAPI is always the provider; upstream backends are
 * never named.
 */
import type { ApiDetail, Endpoint } from '../../features/catalog/model';
import type { DisplayPrice } from '../pricing';
import { quotedRateUsd } from '../pricing';
import { cheapest } from '../catalog';
import { localizedUrl } from '../../i18n/localeHead';
import { breadcrumbList, catalogStep } from './breadcrumb';
import { ORG_ID } from './orgSchema';

/**
 * A USD amount as a plain decimal string.
 *
 * JSON.stringify switches to exponent notation below 1e-6 - `9e-7` instead of
 * `0.0000009` - which is not a valid schema.org price. The live minimum today
 * is $0.00009, so nothing hits that yet, but a cheaper lane would silently
 * emit malformed schema. Six decimals is the same millionths precision
 * lib/pricing.ts already settled on, for the reason documented there: the
 * gateway publishes at most 5 decimal places.
 */
export const priceString = (usd: number): string =>
  usd.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');

/**
 * The offer is quoted per 1,000 of whatever the lane bills for, because that is
 * what the page shows. PriceTag - "the one price display" - headlines a flat
 * lane per 1,000 requests and a metered one per 1,000 results, and structured
 * data that contradicted the visible price would be a defect, not a detail.
 * `referenceQuantity` carries both the count and the unit, so the denomination
 * is explicit rather than implied.
 *
 * No `maxPrice`: the per-request ceiling is a bound on one call, and this
 * specification's terms are 1,000 units, so putting it here would state two
 * denominations inside one price. The page and its FAQ answer both carry the
 * ceiling in words, and the FAQ is in this same graph.
 */
const offer = (price: DisplayPrice, url: string) => {
  const rate = priceString(quotedRateUsd(price));
  return {
    '@type': 'Offer',
    url,
    priceCurrency: 'USD',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      priceCurrency: 'USD',
      price: rate,
      referenceQuantity: {
        '@type': 'QuantitativeValue',
        value: 1000,
        unitText: price.metered && price.unit ? price.unit : 'request',
      },
    },
  };
};

/** Service + Offer + BreadcrumbList graph for one endpoint page. */
export function endpointSchemaGraph(detail: ApiDetail, endpoint: Endpoint, apiId: string) {
  const url = localizedUrl(`/api/${apiId}/${endpoint.id}`);
  const service = {
    '@type': 'Service',
    '@id': `${url}#service`,
    name: `${detail.name} ${endpoint.action} API`,
    description: endpoint.description,
    url,
    provider: { '@id': ORG_ID },
    serviceType: 'Web scraping API',
    areaServed: 'Worldwide',
    offers: offer(cheapest(endpoint).price, url),
  };
  return {
    '@context': 'https://schema.org',
    '@graph': [
      service,
      breadcrumbList(
        catalogStep(),
        { name: detail.name, url: localizedUrl(`/api/${apiId}`) },
        { name: endpoint.action, url },
      ),
    ],
  };
}
